import { ControlConfig, Validators } from '@angular/forms';
import { AvailableLanguages } from '@core/providers/translator/language.contants';
import { CONSTANTS } from './constants';
import { DropdownValue } from './forms/save-form-types';
import { CustomValidators } from './validators/custom-validators';

/**
 * A name is optional wherever one user administers another: leaving it blank lets an operator paste a
 * list of emails straight in, and the backend keeps whatever name the matched user already had. Only
 * someone editing their own profile has to supply one.
 */
export const UserNameValidators = {
  OPTIONAL: [
    Validators.minLength(CONSTANTS.VALIDATION.MIN_LENGTH),
    Validators.maxLength(CONSTANTS.VALIDATION.MAX_LENGTH),
  ],
  REQUIRED: [
    Validators.required,
    Validators.minLength(CONSTANTS.VALIDATION.MIN_LENGTH),
    Validators.maxLength(CONSTANTS.VALIDATION.MAX_LENGTH),
  ],
};

export class AdminDetails {
  avatar = '';
  name = ['', UserNameValidators.OPTIONAL];
  email = [
    '',
    [
      Validators.required,
      Validators.minLength(CONSTANTS.VALIDATION.MIN_LENGTH),
      Validators.maxLength(CONSTANTS.VALIDATION.MAX_LENGTH),
      CustomValidators.email,
    ],
  ];
  phone = ['', [CustomValidators.phoneNumber]];
  prefix: ControlConfig<string> = ['', []];
}
// @todo those forms should be divided/composed better
export class MyProfile extends AdminDetails {
  override name = ['', UserNameValidators.REQUIRED];
  venueDailyReportSubscription: ControlConfig<boolean> = [false, []];
  venueWeeklyReportSubscription: ControlConfig<boolean> = [false, []];
  venueMonthlyReportSubscription: ControlConfig<boolean> = [false, []];
  accountWeeklyReportSubscription: ControlConfig<boolean> = [false, []];
  accountMonthlyReportSubscription: ControlConfig<boolean> = [false, []];
  language = [AvailableLanguages.britishEnglish, [Validators.required]];
}
export const AdminErrors = [
  {
    name: 'required',
    text: 'SHARED.VALIDATION_MSG.REQUIRED',
    rules: ['touched', 'dirty'],
  },
  {
    name: 'minlength',
    text: 'SHARED.VALIDATION_MSG.MIN_LENGTH',
    rules: ['dirty'],
  },
  {
    name: 'maxlength',
    text: 'SHARED.VALIDATION_MSG.MAX_LENGTH',
    translateParams: CONSTANTS.VALIDATION.MAX_LENGTH,
    rules: ['dirty'],
  },
  { name: 'email', text: 'SHARED.VALIDATION_MSG.EMAIL', rules: ['dirty'] },
];
export const AdminUser = {
  AdminDetails,
  AdminErrors,
};

export class MasterManagement {
  isEnabled = [false];
  goLiveDate: ControlConfig<string | null> = [null];
  deactivationDate: ControlConfig<string | null> = [null];
  deactivationReason: ControlConfig<DropdownValue> = [null];
  deactivationComment: ControlConfig<string | null> = [null];
  isTopLevelDashboardEnabled = [false];
  isAiEnabled = [false];
  inputMethods: ControlConfig<string[] | null> = [null];
  quarantineDuration = [4, Validators.required];
  internalComment: ControlConfig<string | null> = [null];
  subscriptionId: ControlConfig<string | null> = [null];
  parentEnterpriseName: ControlConfig<string | null> = [null];
  subscription = [null, [Validators.required]];
  unitType = [null, [Validators.required]];
  csManagerUuid = [null, [Validators.required]];
  questionSet = [null, [Validators.required]];
  vertical = [null, [Validators.required]];
  unitLocation = [null, [Validators.required]];
  childrenType = [null, [Validators.required]];
}

export const MasterManagementErrors = [
  {
    name: 'required',
    text: 'SHARED.VALIDATION_MSG.REQUIRED',
    rules: ['touched', 'dirty'],
  },
];

export const WheelOfFortuneErrors = [
  {
    name: 'required',
    text: 'SHARED.VALIDATION_MSG.REQUIRED_SMALL_VERSION',
    rules: ['touched', 'dirty'],
  },
  {
    name: 'minlength',
    text: 'SHARED.VALIDATION_MSG.MIN_LENGTH',
    rules: ['dirty'],
  },
  {
    name: 'maxlength',
    text: 'SHARED.VALIDATION_MSG.MAX_LENGTH',
    translateParams: CONSTANTS.VALIDATION.MAX_LENGTH,
    rules: ['dirty'],
  },
  {
    name: 'barCode',
    text: 'SHARED.VALIDATION_MSG.BAR_CODE',
    rules: ['touched', 'dirty'],
  },
  {
    name: 'min',
    text: 'SHARED.VALIDATION_MSG.MIN_VALUE',
    translateParams: '0',
    rules: ['touched', 'dirty'],
  },
  {
    name: 'matDatepickerParse',
    text: 'SHARED.VALIDATION_MSG.DATE_WRONG',
    rules: ['touched'],
  },
  {
    name: 'invalidDate',
    text: 'SHARED.VALIDATION_MSG.DATE_WRONG',
    rules: ['touched'],
  },
  {
    name: 'notPastDate',
    text: 'HOME.REWARDS.PAST_DATE_ERROR',
    rules: ['touched'],
  },
];
export const AccountErrors = [
  {
    name: 'required',
    text: 'SHARED.VALIDATION_MSG.REQUIRED',
    rules: ['touched', 'dirty'],
  },
  {
    name: 'minlength',
    text: 'SHARED.VALIDATION_MSG.MIN_LENGTH',
    rules: ['dirty'],
  },
  {
    name: 'maxlength',
    text: 'SHARED.VALIDATION_MSG.MAX_LENGTH',
    translateParams: CONSTANTS.VALIDATION.MAX_LENGTH,
    rules: ['dirty'],
  },
  {
    name: 'barCode',
    text: 'SHARED.VALIDATION_MSG.BAR_CODE',
    rules: ['touched', 'dirty'],
  },
  {
    name: 'isUnique',
    text: 'SHARED.VALIDATION_MSG.UNIQUE_CATEGORY_NAME',
    rules: ['touched', 'dirty'],
  },
  {
    name: 'zipCodeMaxLength',
    text: 'SHARED.VALIDATION_MSG.MAX_LENGTH',
    translateParams: CONSTANTS.VALIDATION.MAX_CODE_LENGTH,
    rules: ['dirty'],
  },
  {
    name: 'zip',
    text: 'SHARED.FORM_ERRORS.INVALID_ZIP',
    rules: ['touched', 'dirty'],
  },
];
export const GroupErrors = [
  {
    name: 'required',
    text: 'SHARED.VALIDATION_MSG.REQUIRED',
    rules: ['touched', 'dirty'],
  },
  {
    name: 'isUnique',
    text: 'SHARED.VALIDATION_MSG.UNIQUE_GROUP_NAME',
    rules: ['touched', 'dirty'],
  },
];
export const CategoryGroupErrors = [
  {
    name: 'required',
    text: 'SHARED.VALIDATION_MSG.REQUIRED',
    rules: ['touched', 'dirty'],
  },
  {
    name: 'isUnique',
    text: 'SHARED.VALIDATION_MSG.UNIQUE_CATEGORY_NAME',
    rules: ['touched', 'dirty'],
  },
];
export const AccountAccount = {
  AccountErrors,
  WheelOfFortuneErrors,
  GroupErrors,
  CategoryGroupErrors,
  MasterManagement,
  MasterManagementErrors,
};
export class AccountUserDetails {
  email = [
    '',
    [
      Validators.minLength(CONSTANTS.VALIDATION.MIN_LENGTH),
      Validators.maxLength(CONSTANTS.VALIDATION.MAX_LENGTH),
      Validators.required,
      CustomValidators.email,
    ],
  ];
  name = ['', UserNameValidators.OPTIONAL];
  phone = ['', [CustomValidators.phoneNumber]];
  prefix: ControlConfig<string> = ['', []];
  // todo: will be handled in future version
  // role = ['', []];
}
export class AccountUserRoles {
  role: ControlConfig<string> = [CONSTANTS.ROLES.USER_REGULAR, []];
}
export const AccountUserErrors = [
  {
    name: 'required',
    text: 'SHARED.VALIDATION_MSG.REQUIRED',
    rules: ['touched', 'dirty'],
  },
  {
    name: 'minlength',
    text: 'SHARED.VALIDATION_MSG.MIN_LENGTH',
    rules: ['dirty'],
  },
  {
    name: 'maxlength',
    text: 'SHARED.VALIDATION_MSG.MAX_LENGTH',
    translateParams: CONSTANTS.VALIDATION.MAX_LENGTH,
    rules: ['dirty'],
  },
  { name: 'email', text: 'SHARED.VALIDATION_MSG.EMAIL', rules: ['dirty'] },
];
export const AccountUser = {
  AccountUserDetails,
  AccountUserErrors,
  AccountUserRoles,
};
export class VenueDetails {
  legalName = [
    '',
    [
      Validators.required,
      Validators.minLength(CONSTANTS.VALIDATION.MIN_LENGTH),
      Validators.maxLength(CONSTANTS.VALIDATION.MAX_LENGTH),
    ],
  ];
  brandName = [
    '',
    [
      Validators.required,
      Validators.minLength(CONSTANTS.VALIDATION.MIN_LENGTH),
      Validators.maxLength(CONSTANTS.VALIDATION.MAX_LENGTH),
    ],
  ];
  number = ['', [Validators.maxLength(CONSTANTS.VALIDATION.MAX_LENGTH)]];
  type: ControlConfig<string> = ['', []];
  manager: ControlConfig<string> = ['', []];
  internalComment: ControlConfig<string> = ['', []];
  quarantineDuration: ControlConfig<number> = [4, []];
}
export class VenueLocation {
  address = [null, [Validators.maxLength(CONSTANTS.VALIDATION.MAX_LENGTH)]];
  code = [null, [CustomValidators.zipCodeMaxLength]];
  city = [null, [Validators.maxLength(CONSTANTS.VALIDATION.MAX_LENGTH)]];
  country = [null, [Validators.required]];
  timezone = [null, [Validators.required]];
}
export class VenueLocationRequired {
  address = [
    Validators.required,
    Validators.minLength(CONSTANTS.VALIDATION.MIN_LENGTH),
    Validators.maxLength(CONSTANTS.VALIDATION.MAX_LENGTH),
  ];
  code = [
    Validators.required,
    Validators.minLength(CONSTANTS.VALIDATION.MIN_LENGTH),
    Validators.maxLength(CONSTANTS.VALIDATION.MAX_LENGTH),
  ];
  country = [Validators.required];
  city = [
    Validators.required,
    Validators.minLength(CONSTANTS.VALIDATION.MIN_LENGTH),
    Validators.maxLength(CONSTANTS.VALIDATION.MAX_LENGTH),
  ];
}
export const VenueErrors = {
  general: [
    {
      name: 'required',
      text: 'SHARED.VALIDATION_MSG.REQUIRED',
      rules: ['touched', 'dirty'],
    },
    {
      name: 'minlength',
      text: 'SHARED.VALIDATION_MSG.MIN_LENGTH',
      rules: ['dirty'],
    },
    {
      name: 'maxlength',
      text: 'SHARED.VALIDATION_MSG.MAX_LENGTH',
      translateParams: CONSTANTS.VALIDATION.MAX_LENGTH,
      rules: ['dirty'],
    },
    { name: 'url', text: 'SHARED.VALIDATION_MSG.URL', rules: ['dirty'] },
  ],
  code: [
    {
      name: 'minlength',
      text: 'SHARED.VALIDATION_MSG.MIN_LENGTH',
      rules: ['dirty'],
    },
    {
      name: 'zip',
      translateKey: 'SHARED.FORM_ERRORS.INVALID_ZIP',
      rules: ['dirty'],
    },
    {
      name: 'required',
      translateKey: 'SHARED.FORM_ERRORS.ZIP_REQUIRED_IF_LOCATION_FILLED',
      rules: ['touched', 'dirty'],
    },
  ],
  address: [
    {
      name: 'minlength',
      text: 'SHARED.VALIDATION_MSG.MIN_LENGTH',
      rules: ['dirty'],
    },
    {
      name: 'maxlength',
      text: 'SHARED.VALIDATION_MSG.MAX_LENGTH',
      translateParams: CONSTANTS.VALIDATION.MAX_LENGTH,
      rules: ['dirty'],
    },
  ],
  city: [
    {
      name: 'minlength',
      text: 'SHARED.VALIDATION_MSG.MIN_LENGTH',
      rules: ['dirty'],
    },
    {
      name: 'maxlength',
      text: 'SHARED.VALIDATION_MSG.MAX_LENGTH',
      translateParams: CONSTANTS.VALIDATION.MAX_LENGTH,
      rules: ['dirty'],
    },
  ],
  number: [
    {
      name: 'pattern',
      text: 'SHARED.VALIDATION_MSG.INCORRECT_FORMAT',
      rules: ['dirty'],
    },
  ],
  categoryName: [
    {
      name: 'required',
      text: 'SHARED.VALIDATION_MSG.REQUIRED',
      rules: ['touched', 'dirty'],
    },
    {
      name: 'minlength',
      text: 'SHARED.VALIDATION_MSG.MIN_LENGTH',
      translateParams: CONSTANTS.VALIDATION.CATEGORY_NAME.MIN_LENGTH,
      rules: ['dirty'],
    },
    {
      name: 'maxlength',
      text: 'SHARED.VALIDATION_MSG.MAX_LENGTH',
      translateParams: CONSTANTS.VALIDATION.CATEGORY_NAME.MAX_LENGTH,
      rules: ['dirty'],
    },
    {
      name: 'whitespace',
      text: 'SHARED.VALIDATION_MSG.WHITE_SPACES',
      rules: ['dirty'],
    },
  ],
};
export const VenueAccount = {
  VenueDetails,
  VenueLocation,
  VenueLocationRequired,
  VenueErrors,
};
export const VenueUserEmailValidators = {
  OPTIONAL: [
    Validators.minLength(CONSTANTS.VALIDATION.MIN_LENGTH),
    Validators.maxLength(CONSTANTS.VALIDATION.MAX_LENGTH),
    CustomValidators.optionalEmail,
  ],
  REQUIRED: [
    Validators.required,
    Validators.minLength(CONSTANTS.VALIDATION.MIN_LENGTH),
    Validators.maxLength(CONSTANTS.VALIDATION.MAX_LENGTH),
    CustomValidators.email,
  ],
};
export class VenueUserDetails {
  avatar = '';
  name = ['', UserNameValidators.OPTIONAL];
  emailOptional = ['', VenueUserEmailValidators.OPTIONAL];
  emailRequired = ['', VenueUserEmailValidators.REQUIRED];
  phone = ['', [CustomValidators.phoneNumber]];
  prefix: ControlConfig<string> = ['', []];
  isRegularUser: ControlConfig<boolean> = [{ value: true, disabled: true }, []];
}
export class VenueUserRoles {
  role: ControlConfig<string> = [CONSTANTS.ROLES.USER_REGULAR, []];
}
export const VenueUserErrors = [
  {
    name: 'required',
    text: 'SHARED.VALIDATION_MSG.REQUIRED',
    rules: ['touched', 'dirty'],
  },
  {
    name: 'minlength',
    text: 'SHARED.VALIDATION_MSG.MIN_LENGTH',
    rules: ['dirty'],
  },
  {
    name: 'maxlength',
    text: 'SHARED.VALIDATION_MSG.MAX_LENGTH',
    translateParams: CONSTANTS.VALIDATION.MAX_LENGTH,
    rules: ['dirty'],
  },
  { name: 'email', text: 'SHARED.VALIDATION_MSG.EMAIL', rules: ['dirty'] },
];
export const GeneralErrors = [{ name: 'url', text: 'SHARED.VALIDATION_MSG.URL', rules: ['dirty'] }];
export const GeneralQuestionsError = [
  {
    name: 'required',
    text: 'SHARED.VALIDATION_MSG.REQUIRED',
    rules: ['touched'],
  },
  {
    name: 'dateRange',
    text: 'SHARED.VALIDATION_MSG.REQUIRED',
    rules: ['touched'],
  },
  {
    name: 'maxlength',
    text: 'SHARED.VALIDATION_MSG.MAX_LENGTH',
    translateParams: CONSTANTS.VALIDATION.ONE_MORE_QUESTION.QUESTION_MAX_LENGTH,
    rules: ['dirty'],
  },
  {
    name: 'whitespace',
    text: 'SHARED.VALIDATION_MSG.WHITE_SPACES',
    rules: ['touched'],
  },
  {
    name: 'min',
    text: 'SHARED.VALIDATION_MSG.MIN_VALUE',
    translateParams: '0',
    rules: ['touched', 'dirty'],
  },
  {
    name: 'max',
    text: 'SHARED.VALIDATION_MSG.MAX_VALUE',
    translateParams: '0',
    rules: ['touched', 'dirty'],
  },
  {
    name: 'matDatepickerParse',
    text: 'SHARED.VALIDATION_MSG.DATE_WRONG',
    rules: ['touched'],
  },
  {
    name: 'matEndDateInvalid',
    text: 'SHARED.VALIDATION_MSG.DATE_RANGE_WRONG_VALUE',
    rules: ['touched'],
  },
];
export const OneMoreQuestionsFormatErrors = [
  {
    name: 'required',
    text: 'SHARED.VALIDATION_MSG.REQUIRED',
    rules: ['touched'],
  },
  {
    name: 'maxlength',
    text: 'SHARED.VALIDATION_MSG.MAX_LENGTH',
    translateParams: CONSTANTS.VALIDATION.ONE_MORE_QUESTION.FORMAT_OPTIONS_LENGTH,
    rules: ['dirty', 'touched'],
  },
  {
    name: 'whitespace',
    text: 'SHARED.VALIDATION_MSG.WHITE_SPACES',
    rules: ['dirty'],
  },
];
export const VenueUser = {
  VenueUserDetails,
  VenueUserErrors,
  VenueUserRoles,
};
export class AccountStatementsGroupName {
  groupName = ['', [Validators.required]];
}
export class AccountStatementsTranslations {
  en: ControlConfig<string> = ['', []];
  no: ControlConfig<string> = ['', []];
  se: ControlConfig<string> = ['', []];
}
export const AccountStatement = {
  AccountStatementsGroupName,
  AccountStatementsTranslations,
};
// External export
export const FormModels = {
  AdminUser,
  AccountAccount,
  AccountUser,
  VenueAccount,
  VenueUser,
  AccountStatement,
};
